<template>
  <div class="ui-input" :class="[
    `ui-input--${size}`,
    {
      'ui-input--disabled': disabled,
      'ui-input--readonly': readonly, // readonly 클래스 추가
      'ui-input--error': error,
      'ui-input--block': block,
      'ui-input--with-icon': icon,
      'ui-input--with-prefix': prefix,
      'ui-input--with-suffix': suffix,
      'ui-input--with-prefix-icon': prefixIcon,
      'ui-input--with-suffix-icon': suffixIcon,
      'ui-input--has-value': inputHasValue,
      'ui-input--focused': isFocused,
    },
  ]">
    <label v-if="label" class="ui-input__label" :for="id">
      {{ label }}
      <span v-if="required" class="ui-input__required">*</span>
    </label>

    <div
      class="ui-input__wrapper"
      :data-invalid-eval-anchor="invalidEvalAnchor || undefined"
    >
      <div v-if="prefix" class="ui-input__prefix">{{ prefix }}</div>
      <i v-if="prefixIcon" :class="`icon icon-${prefixIcon} icon-md`" class="ui-input__prefix-icon"></i>
      <i v-if="icon" :class="`icon icon-${icon} icon-md`" class="ui-input__icon"></i>

      <input ref="inputRef" :id="id" :type="resolvedType" :value="modelValue" :placeholder="fieldPlaceholder" :disabled="disabled"
        :required="required" :readonly="readonly" :maxlength="maxlength" :min="min" :max="max" :step="step" :name="name"
        :autocomplete="autocomplete" :inputmode="integerOnly ? 'numeric' : undefined" class="ui-input__field" :class="{
          'ui-input__field--with-prefix-icon': prefixIcon,
          'ui-input__field--with-clear': showClearButton,
          'ui-input__field--with-password-toggle': showPasswordToggle,
        }" @input="handleInput" @focus="handleFocus" @blur="handleBlur" @change="handleChange" />

      <i v-if="suffixIcon" :class="`icon icon-${suffixIcon} icon-md`" class="ui-input__suffix-icon"></i>
      <div v-if="suffix" class="ui-input__suffix">{{ suffix }}</div>
      <button
        v-if="showPasswordToggle"
        type="button"
        class="ui-input__password-toggle"
        :class="{ 'ui-input__password-toggle--with-clear': showClearButton }"
        :aria-label="isPasswordVisible ? t('common.hidePassword') : t('common.showPassword')"
        @mousedown.prevent
        @click="togglePasswordVisible"
      >
        <i :class="isPasswordVisible ? 'icon icon-eye-off icon-md' : 'icon icon-eye-open icon-md'"></i>
      </button>
      <i v-if="showClearButton" class="icon icon-x-circle icon-md ui-input__clear" @click="clearInput"></i>
    </div>

    <div v-if="error && errorMessage" class="ui-input__error">{{ errorMessage }}</div>
    <div v-else-if="helperText" class="ui-input__helper">{{ helperText }}</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { lengthbCut, sanitizeNumericUnsigned } from '~/composables/useCommon.js';

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: value => ['text', 'password', 'email', 'number', 'tel', 'url', 'search', 'date', 'time', 'datetime-local'].includes(value),
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value),
  },
  icon: {
    type: String,
    default: '',
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  suffixIcon: {
    type: String,
    default: '',
  },
  prefix: {
    type: String,
    default: '',
  },
  suffix: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  /** type="password"일 때 X 버튼 왼쪽에 보기/숨기기 눈 아이콘을 표시 */
  passwordToggle: {
    type: Boolean,
    default: false,
  },
  block: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  helperText: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: () => `ui-input-${Math.random().toString(36).substring(2, 9)}`,
  },
  name: {
    type: String,
    default: '',
  },
  maxlength: {
    type: [String, Number],
    default: undefined,
  },
  /** UTF-8 바이트 기준 최대 길이. 한도 도달 후 추가 입력 시에도 DOM과 동기화하려면 부모에서 lengthbCut만 쓰지 말고 이 prop 사용 */
  maxBytes: {
    type: Number,
    default: undefined,
  },
  min: {
    type: [String, Number],
    default: undefined,
  },
  max: {
    type: [String, Number],
    default: undefined,
  },
  step: {
    type: [String, Number],
    default: undefined,
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  /** true면 숫자·소수점만(음수 부호 불가, 소수점 1회) — 가중치·점수 등 */
  unsigned: {
    type: Boolean,
    default: false,
  },
  /** true면 0~9 정수만 허용 (콤마·소수점·음수 부호 제외) — 토큰 한도 등 */
  integerOnly: {
    type: Boolean,
    default: false,
  },
  /** 평가 유효성 실패 시 스크롤·강조용 — 보이는 입력 테두리(.ui-input__wrapper)에 앵커 부여 */
  invalidEvalAnchor: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'change']);

// 내부 상태로 입력값 추적
const inputHasValue = ref(false);

// focus 상태 추적을 위한 새로운 ref 추가
const isFocused = ref(false);

const fieldPlaceholder = computed(() => (props.readonly || props.disabled ? '' : props.placeholder));

// X 버튼 표시 여부 계산
const showClearButton = computed(() => {
  return props.clearable && !!props.modelValue && !props.readonly && !props.disabled;
});

const isPasswordVisible = ref(false);
const showPasswordToggle = computed(() => {
  return props.passwordToggle && props.type === 'password' && !props.readonly && !props.disabled;
});
const resolvedType = computed(() => {
  if (showPasswordToggle.value && isPasswordVisible.value) return 'text';
  return props.type;
});

const togglePasswordVisible = () => {
  isPasswordVisible.value = !isPasswordVisible.value;
};

const clearInput = () => {
  emit('update:modelValue', '');
  inputHasValue.value = false;
  isFocused.value = true;

  // input 요소에 focus 설정
  const inputEl = document.querySelector(`#${props.id}`);
  if (inputEl) {
    inputEl.focus();
    isFocused.value = true;
  }
};

// 이벤트 핸들러 함수들
const handleInput = event => {
  let newValue = event.target.value;
  if (props.integerOnly) {
    newValue = newValue.replace(/,/g, '').replace(/\D/g, '');
    event.target.value = newValue;
  } else if (props.unsigned) {
    newValue = sanitizeNumericUnsigned(newValue);
    event.target.value = newValue;
  } else if (props.type === 'number') {
    // type="number"일 때 숫자 외 문자 제거
    newValue = newValue.replace(/[^0-9.-]/g, '');
    event.target.value = newValue;
    // max/min은 클릭(step)에는 먹히나 직접 입력은 브라우저에 따라 무시될 수 있어 동일하게 클램프
    if (newValue !== '' && newValue !== '-' && newValue !== '.' && newValue !== '-.') {
      const num = Number(newValue);
      if (!Number.isNaN(num) && Number.isFinite(num)) {
        let clamped = num;
        if (props.max !== undefined && props.max !== '' && props.max !== null) {
          const maxN = Number(props.max);
          if (!Number.isNaN(maxN)) clamped = Math.min(clamped, maxN);
        }
        if (props.min !== undefined && props.min !== '' && props.min !== null) {
          const minN = Number(props.min);
          if (!Number.isNaN(minN)) clamped = Math.max(clamped, minN);
        }
        if (clamped !== num) {
          newValue = String(clamped);
          event.target.value = newValue;
        }
      }
    }
  } else if (props.maxBytes != null && props.maxBytes > 0) {
    const trimmed = lengthbCut(String(newValue ?? ''), Number(props.maxBytes));
    if (trimmed !== newValue) {
      event.target.value = trimmed;
      newValue = trimmed;
    }
  } else if (props.maxlength != null && props.maxlength !== '') {
    // HTML maxlength는 한글 IME 조합 중 우회될 수 있어 JS로 재강제
    const maxLen = Number(props.maxlength);
    if (!Number.isNaN(maxLen) && maxLen > 0 && String(newValue).length > maxLen) {
      newValue = String(newValue).slice(0, maxLen);
      event.target.value = newValue;
    }
  }
  inputHasValue.value = newValue.length > 0;
  emit('update:modelValue', newValue);
};

const handleFocus = event => {
  isFocused.value = true;
  emit('focus', event);
};

const handleBlur = event => {
  isFocused.value = false;
  emit('blur', event);
};

const handleChange = event => {
  emit('change', event);
};

// input 요소에 대한 ref
const inputRef = ref(null);

// autofocus가 true일 때 마운트 시 포커스
onMounted(() => {
  if (props.autofocus && inputRef.value) {
    inputRef.value.focus();
  }
});

// 외부에서 focus 메서드 호출 가능하도록 expose
const focus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({
  focus,
});
</script>

<style lang="scss" scoped>
@use 'sass:color';

.ui-input {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
  width: 100%;

  // 값이 입력되었을 때 스타일
  &--has-value {
    .ui-input__wrapper {
      border: 1px solid $border-color-filled;
    }
  }

  &__label {
    font-size: $font-size-sm;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);
    margin-bottom: 4px;
  }

  &__required {
    color: var(--color-system-r30, #f30);
    margin-left: 2px;
  }

  &__wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: $border-radius-sm;
    border: 1px solid $border-color-light;
    background: var(--color-gray-0, #fff);
    transition: border-color $transition-normal ease;

    &:focus-within {
      border-color: var(--color-system-b30, #0084ff);
    }
  }

  &__prefix-icon {
    color: var(--color-gray-40, #8a949e);
    font-size: $font-size-xl;
    margin-right: 10px;
    filter: invert(91%) sepia(4%) saturate(129%) hue-rotate(184deg) brightness(94%) contrast(88%);
  }

  &__suffix-icon {
    color: var(--color-gray-40, #8a949e);
    font-size: $font-size-xl;
    margin-left: 5px;
  }


  &__field {
    flex: 1;
    min-width: 0; // flex item shrink 허용
    border: none;
    background: transparent;
    font-size: $font-size-sm;
    color: var(--color-gray-70, #464c53);
    outline: none;
    height: 100%;

    &::placeholder {
      color: $placeholder-color;
      font-family: $placeholder-font-family;
      font-size: $ui-font-md;
      font-style: normal;
      font-weight: $placeholder-font-weight;
      line-height: $placeholder-line-height;
    }

    &--with-prefix-icon {
      padding-left: 6px;
    }

    &--with-clear {
      padding-right: 22px;
    }

    &--with-password-toggle {
      padding-right: 28px;
    }

    &--with-password-toggle#{&}--with-clear {
      padding-right: 50px;
    }

    &[type='number'] {
      -moz-appearance: textfield;
      appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  &__icon,
  &__clear {
    flex-shrink: 0;
    color: var(--color-gray-70, #464c53);
  }

  &__clear {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    flex-shrink: 0;
    cursor: pointer;
    color: var(--color-gray-40, #8a949e);
    transition: color $transition-normal ease;
    z-index: 1;

    &:hover {
      color: var(--color-gray-60, #58616a);
    }
  }

  &__password-toggle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    z-index: 1;

    &--with-clear {
      right: 34px;
    }

    &:hover .icon {
      filter: brightness(0.7);
    }
  }

  &__prefix,
  &__suffix {
    color: var(--color-gray-70, #464c53);
    font-size: $font-size-sm;
    padding-right: $spacing-sm;
  }

  // suffix나 suffixIcon이 있을 때 X 버튼 위치 조정
  &--with-suffix,
  &--with-suffix-icon {
    .ui-input__clear {
      right: 40px;
    }
  }

  &__error,
  &__helper {
    margin-top: 2px;
  }

  &__error {
    font-size: $font-size-sm;
    color: $system-red;
  }

  &__helper {
    font-size: $font-size-xs;
    color: var(--color-gray-60, #58616a);
  }

  // 크기 변형 - Small (32px)
  &--small {
    .ui-input__wrapper {
      height: $ui-height-sm; // 32px
      padding: $ui-padding-sm;
    }

    .ui-input__field {
      font-size: $ui-font-sm;

      &::placeholder {
        font-size: $ui-font-sm;
      }
    }

    .ui-input__label {
      @include font-style($body-small);
    }
  }

  // 크기 변형 - Medium (40px)
  &--medium {
    .ui-input__wrapper {
      height: $ui-height-md; // 40px
      padding: $ui-padding-md;
      width: 100%;
    }

    .ui-input__field {
      font-size: $ui-font-md;

      &::placeholder {
        font-size: $ui-font-md;
      }
    }

    .ui-input__label {
      @include font-style($body-medium);
    }
  }

  // 크기 변형 - Large (48px)
  &--large {
    .ui-input__wrapper {
      height: $ui-height-lg; // 48px
      padding: $ui-padding-lg;
    }

    .ui-input__field {
      font-size: $ui-font-lg;

      &::placeholder {
        font-size: $ui-font-lg;
      }
    }

    .ui-input__label {
      @include font-style($body-medium);
    }
  }

  // 크기 변형 - XLarge (56px)
  &--xlarge {
    .ui-input__wrapper {
      height: $ui-height-xl; // 56px
      padding: $ui-padding-xl;
    }

    .ui-input__field {
      font-size: $ui-font-xl;

      &::placeholder {
        font-size: $ui-font-xl;
      }
    }

    .ui-input__label {
      @include font-style($body-xlarge-bold);
      font-size: $font-size-xxl;
    }
  }

  // Block
  &--block {
    .ui-input__wrapper {
      width: 100%;
    }
  }

  // 상태 변형
  &--disabled {
    .ui-input__wrapper {
      border: 1px solid var(--color-gray-20, #cdd1d5);
      background: var(--color-gray-10, #e6e8ea);
      color: var(--color-gray-30, #b1b8be);
      cursor: not-allowed;
    }

    .ui-input__field {
      cursor: not-allowed;
      color: var(--color-gray-30, #b1b8be);
    }
  }

  // readonly 상태 스타일 추가
  &--readonly {
    .ui-input__wrapper {
      background: var(--color-gray-10, #e6e8ea); // 읽기 전용 배경
      border: 1px solid $border-color-light;
    }

    .ui-input__field {
      background: transparent;
      color: var(--color-gray-60, #58616a);
      cursor: default;
    }
  }

  &--error {
    .ui-input__wrapper {
      border: 1px solid var(--color-system-r30, #f30);
      color: var(--color-gray-70, #464c53);

      &:focus-within {
        box-shadow: 0 0 0 2px rgba(255, 51, 0, 0.1);
      }
    }
  }

  // Modifiers
  &--with-icon,
  &--with-prefix,
  &--with-suffix,
  &--with-prefix-icon,
  &--with-suffix-icon {
    .ui-input__field {
      padding-left: 0;
    }
  }
}

// 반응형 스타일
@media (max-width: 768px) {
  .ui-input {
    &--small {
      .ui-input__wrapper {
        padding: 5px 10px;
      }
    }

    &--medium {
      .ui-input__wrapper {
        padding: 6px 12px;
      }
    }

    &--large {
      .ui-input__wrapper {
        padding: 8px 14px;
      }
    }

    &--xlarge {
      .ui-input__wrapper {
        padding: 10px 16px;
      }
    }
  }
}
</style>
